import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to read a cookie value by name
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Attach CSRF token from cookie for state-changing requests
    const csrfToken = getCookie('_csrf');
    if (csrfToken && !['get', 'head', 'options'].includes(config.method)) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Token refresh state
let isRefreshing = false;
let isRedirecting = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

function forceLogout() {
  if (isRedirecting) return;
  isRedirecting = true;
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('auth-storage');
  delete api.defaults.headers.common['Authorization'];
  // Don't redirect if we're already on the login page
  if (!window.location.pathname.includes('/login')) {
    window.location.replace('/login');
  }
}

// Response interceptor with token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If we're already redirecting to login, reject immediately
    if (isRedirecting) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh if this was itself a refresh or login request
      if (originalRequest.url === '/auth/refresh' || originalRequest.url === '/auth/login') {
        // For login requests, don't force logout - let the component handle the error
        if (originalRequest.url === '/auth/login') {
          return Promise.reject(error);
        }
        // For refresh failures, force logout
        forceLogout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        forceLogout();
        return Promise.reject(error);
      }

      try {
        const response = await axios.post('/api/auth/refresh', { refreshToken });
        const { token, refreshToken: newRefreshToken } = response.data;

        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', newRefreshToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        processQueue(null, token);

        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ============ Image URL Helpers ============
// Helper functions to convert relative URLs to full URLs for images
// Matches mobile app implementation for consistency

const getBaseUrl = () => {
  // Try environment variable first
  let apiUrl = import.meta.env.VITE_API_URL;
  const isBrowser = typeof window !== 'undefined';
  const isLocalHostReq = apiUrl && String(apiUrl).includes('localhost');
  const onCloud = isBrowser && !window.location.hostname.includes('localhost') && window.location.hostname !== '127.0.0.1';

  // Self-Healing logic: 
  // 1. If env var is missing
  // 2. OR if env var says 'localhost' but the browser is NOT on localhost (Cloud deployment)
  if ((!apiUrl || (isLocalHostReq && onCloud)) && isBrowser) {
    apiUrl = `${window.location.origin}/api`;
  } else if (!apiUrl) {
    apiUrl = 'http://localhost:3001';
  }

  // Ensure it's a full string and remove /api suffix to get base URL for uploads
  return String(apiUrl).replace(/\/api$/, '');
};

/**
 * Backend now returns presigned MinIO URLs for alert snapshots, so the frontend
 * just passes them through. Kept as helpers for legacy call sites and for the
 * occasional camera/avatar reference that still resolves through the API origin.
 */
export const getImageUrl = (value) => {
  if (!value) return null;
  if (value.startsWith('http') || value.startsWith('data:')) return value;
  const baseUrl = getBaseUrl();
  return `${baseUrl}${value.startsWith('/') ? '' : '/'}${value}`;
};

export const getThumbnailUrl = (alert) => {
  if (!alert) return null;
  return alert.thumbnailUrl || alert.snapshotUrl || null;
};

export const getAlertImageUrl = (alert /* preferFull unused — backend chooses */) => {
  if (!alert) return null;
  return alert.snapshotUrl || alert.thumbnailUrl || null;
};

export default api;

// API helper functions
export const dashboardAPI = {
  getSummary: (siteId, dateRange) => api.get('/dashboard/summary', {
    params: { siteId, startDate: dateRange?.startDate, endDate: dateRange?.endDate },
  }),
  getActiveAlerts: (siteId) => api.get('/dashboard/alerts/live', { params: { siteId } }),
  getHourlyCompliance: (siteId, date) => api.get('/dashboard/compliance/hourly', {
    params: { siteId, date },
  }),
  getCameraStatus: (siteId) => api.get('/dashboard/cameras/status', { params: { siteId } }),
  getZoneOccupancy: (siteId) => api.get('/dashboard/zones/occupancy', { params: { siteId } }),
  getDetectionSamples: (siteId) => api.get('/dashboard/detection-samples', { params: { siteId } }),
  getDailyStats: (siteId, days, dateRange) => api.get('/dashboard/stats/daily', {
    params: { siteId, days, startDate: dateRange?.startDate, endDate: dateRange?.endDate },
  }),
  getLeaderboard: () => api.get('/dashboard/leaderboard'),
};

export const alertsAPI = {
  getAlerts: (params) => api.get('/alerts', { params }),
  getActiveAlerts: (siteId) => api.get('/alerts/active', { params: { siteId } }),
  getAlertStats: (siteId, period) => api.get('/alerts/stats', { params: { siteId, period } }),
  getAlert: (id) => api.get(`/alerts/${id}`),
  getRelatedAlerts: (id, windowMinutes = 60) => api.get(`/alerts/${id}/related`, { params: { windowMinutes } }),
  acknowledgeAlert: (id) => api.put(`/alerts/${id}/acknowledge`),
  resolveAlert: (id, data) => api.put(`/alerts/${id}/resolve`, data),
  markFalsePositive: (id, data) => api.put(`/alerts/${id}/false-positive`, data),
  reactivateAlert: (id) => api.put(`/alerts/${id}/reactivate`),
  escalateAlert: (id, data) => api.put(`/alerts/${id}/escalate`, data),
  bulkAcknowledge: (alertIds) => api.post('/alerts/bulk-acknowledge', { alertIds }),
  bulkResolve: (alertIds, resolution) => api.post('/alerts/bulk-resolve', { alertIds, resolution }),
  resolveAll: (body = {}) => api.post('/alerts/resolve-all', body),
  bulkFalsePositive: (alertIds) => api.post('/alerts/bulk-false-positive', { alertIds }),
  exportAlerts: (params) => api.get('/alerts/export', { params, responseType: 'blob' }),
};

// Per-camera training data (label review queue, stats, models). Phase 1: label + view.
export const trainingAPI = {
  getItems: (cameraId, params) => api.get(`/training/cameras/${cameraId}/items`, { params }),
  labelSample: (cameraId, body) => api.post(`/training/cameras/${cameraId}/samples`, body),
  getStats: (cameraId) => api.get(`/training/cameras/${cameraId}/stats`),
  getModels: (cameraId) => api.get(`/training/cameras/${cameraId}/models`),
  startRun: (cameraId, body) => api.post(`/training/cameras/${cameraId}/run`, body),
  getRuns: (cameraId) => api.get(`/training/cameras/${cameraId}/runs`),
  getRun: (runId) => api.get(`/training/runs/${runId}`),
  promoteModel: (modelId) => api.post(`/training/models/${modelId}/promote`),
  archiveModel: (modelId) => api.post(`/training/models/${modelId}/archive`),
};

export const sitesAPI = {
  getSites: (params) => api.get('/sites', { params }),
  getSite: (id) => api.get(`/sites/${id}`),
  createSite: (data) => api.post('/sites', data),
  updateSite: (id, data) => api.put(`/sites/${id}`, data),
  deleteSite: (id) => api.delete(`/sites/${id}`),
  getSiteMetrics: (id) => api.get(`/sites/${id}/metrics`),
  getSiteCompliance: (id, period) => api.get(`/sites/${id}/compliance`, { params: { period } }),
};

export const camerasAPI = {
  getCameras: (siteId, options = {}) => api.get('/cameras', { params: { siteId, ...options } }),
  getCamera: (id) => api.get(`/cameras/${id}`),
  getSnapshot: (id) => api.get(`/cameras/${id}/snapshot`),
  createCamera: (data) => api.post('/cameras', data),
  updateCamera: (id, data) => api.put(`/cameras/${id}`, data),
  // Soft delete (restorable) — sets isActive=false + deletedAt and notifies the AI engine
  deleteCamera: (id) => api.delete(`/cameras/${id}`),
  // Permanent delete — removes the row when possible (no FK-constrained records)
  deleteCameraPermanent: (id) => api.delete(`/cameras/${id}?permanent=true`),
  // Restore a soft-deleted camera — reactivates it and notifies the AI engine
  restoreCamera: (id) => api.post(`/cameras/${id}/restore`),
  // List soft-deleted cameras (for the Deleted Cameras page)
  getDeletedCameras: (siteId, { page, limit } = {}) => api.get('/cameras', { params: { siteId, deleted: 'true', page, limit } }),
  // ONVIF discovery (#32)
  discoverCameras: (aiDeviceId) => api.post('/cameras/discover', { aiDeviceId }),
  getOnvifInfo: (ip, port, username, password) => api.post('/cameras/onvif-info', { ip, port, username, password }),
  // Enhanced ONVIF discovery with port scanning
  startDiscoveryScan: (params) => api.post('/discovery/scan', params),
  getDiscoveryScanStatus: (scanSessionId) => api.get(`/discovery/scan/${scanSessionId}`),
  getDiscoveredCameras: (siteId) => api.get('/discovery/cameras', { params: { siteId } }),
};

export const incidentsAPI = {
  getIncidents: (params) => api.get('/incidents', { params }),
  getIncident: (id) => api.get(`/incidents/${id}`),
  createIncident: (data) => api.post('/incidents', data),
  updateIncident: (id, data) => api.put(`/incidents/${id}`, data),
};

export const notificationsAPI = {
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (data) => api.put('/notifications/settings', data),
  testNotification: (channel) => api.post('/notifications/test', { channel }),
  getHistory: (params) => api.get('/notifications/history', { params }),
  getStats: () => api.get('/notifications/stats'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  registerPushToken: (token, platform) => api.post('/notifications/push-token', { token, platform }),
  // Escalation rules (note: siteId is required)
  getEscalationRules: (siteId) => api.get('/notifications/escalation-rules', { params: { siteId } }),
  createEscalationRule: (data) => api.post('/notifications/escalation-rules', data),
  updateEscalationRule: (id, data) => api.put(`/notifications/escalation-rules/${id}`, data),
  deleteEscalationRule: (id) => api.delete(`/notifications/escalation-rules/${id}`),
  // Mail alert rules (org-wide: category+severity -> roles -> cooldown)
  getMailAlertRules: () => api.get('/notifications/mail-alert-rules'),
  updateMailAlertRule: (category, severity, data) => api.put(`/notifications/mail-alert-rules/${category}/${severity}`, data),
  getMailAlertUsage: () => api.get('/notifications/mail-alert-usage'),
};

export const analyticsAPI = {
  // Combined endpoint - fetches all analytics in one request
  getAll: (params) => api.get('/analytics/all', { params }),
  // Individual endpoints (kept for backwards compatibility)
  getComplianceTrends: (params) => api.get('/analytics/compliance-trends', { params }),
  getViolationHeatmap: (params) => api.get('/analytics/violation-heatmap', { params }),
  getZoneStats: (params) => api.get('/analytics/zone-stats', { params }),
  getCameraPerformance: (params) => api.get('/analytics/camera-performance', { params }),
  getAlertBreakdown: (params) => api.get('/analytics/alert-breakdown', { params }),
  getResponseTimes: (params) => api.get('/analytics/response-times', { params }),
  getDailySummary: (params) => api.get('/analytics/daily-summary', { params }),
  getIncidents: (params) => api.get('/analytics/incidents', { params }),
  getOshaMetrics: (params) => api.get('/analytics/osha-metrics', { params }),
  exportData: (params) => api.get('/analytics/export', { params, responseType: 'blob' }),
};

export const reportsAPI = {
  // List all reports
  getReports: (params) => api.get('/reports', { params }),

  // Generate report by type (daily, weekly, monthly, compliance, incident)
  generateReport: (type, params) => api.get(`/reports/generate/${type}`, { params }),

  // Get daily report for specific date
  getDailyReport: (date, siteId) => api.get(`/reports/daily/${date}`, { params: { siteId } }),

  // Get summary statistics
  getSummary: (params) => api.get('/reports/summary', { params }),

  // Export reports (JSON or CSV)
  exportReports: (params) => {
    if (params.format === 'csv') {
      return api.get('/reports/export', { params, responseType: 'blob' });
    }
    return api.get('/reports/export', { params });
  },

  // Backfill historical reports
  backfillReports: (siteId, days) => api.post('/reports/backfill', { siteId, days }),
};

export const auditLogAPI = {
  getLogs: (params) => api.get('/audit-log', { params }),
  getActions: () => api.get('/audit-log/actions'),
  getConfigChanges: (params) => api.get('/audit-log/config-changes', { params }),
};

export const reportScheduleAPI = {
  list: () => api.get('/report-schedules'),
  create: (data) => api.post('/report-schedules', data),
  update: (id, data) => api.put(`/report-schedules/${id}`, data),
  delete: (id) => api.delete(`/report-schedules/${id}`),
};

export const webhooksAPI = {
  list: () => api.get('/webhooks'),
  create: (data) => api.post('/webhooks', data),
  update: (id, data) => api.put(`/webhooks/${id}`, data),
  delete: (id) => api.delete(`/webhooks/${id}`),
  test: (id) => api.post(`/webhooks/${id}/test`),
  rotateSecret: (id) => api.post(`/webhooks/${id}/rotate-secret`),
};

export const searchAPI = {
  search: (params) => api.get('/search', { params }),
  reindex: () => api.post('/search/reindex'),
};

export const riskAPI = {
  getSiteRiskScore: (siteId) => api.get(`/risk/scores/site/${siteId}`),
  getScoresOverview: (params) => api.get('/risk/scores/overview', { params }),
  calculateScores: (data) => api.post('/risk/scores/calculate', data),
  getComplianceForecast: (params) => api.get('/risk/forecast/compliance', { params }),
  getAlertRateForecast: (params) => api.get('/risk/forecast/alert-rate', { params }),
  getAllForecasts: (params) => api.get('/risk/forecast/all', { params }),
  getAnomalies: (params) => api.get('/risk/anomalies', { params }),
  acknowledgeAnomaly: (id) => api.patch(`/risk/anomalies/${id}/acknowledge`),
  getAnomalyTimeline: (params) => api.get('/risk/anomalies/timeline', { params }),
  getDashboard: (params) => api.get('/risk/dashboard', { params }),
  getScoresHistory: (params) => api.get('/risk/scores/history', { params }),
};

export const zonesAPI = {
  getZones: (params) => api.get('/zones', { params }),
  getZone: (id) => api.get(`/zones/${id}`),
  createZone: (data) => api.post('/zones', data),
  updateZone: (id, data) => api.put(`/zones/${id}`, data),
  deleteZone: (id) => api.delete(`/zones/${id}`),
  getOccupancy: (id, hours) => api.get(`/zones/${id}/occupancy`, { params: { hours } }),
  getViolations: (id, params) => api.get(`/zones/${id}/violations`, { params }),
  getCurrentRules: (id) => api.get(`/zones/${id}/current-rules`),
  updateTimeRules: (id, rules) => api.put(`/zones/${id}/time-rules`, { rules }),
  assignCamera: (zoneId, data) => api.post(`/zones/${zoneId}/cameras`, data),
  unassignCamera: (zoneId, cameraId) => api.delete(`/zones/${zoneId}/cameras/${cameraId}`),
};

export const benchmarkingAPI = {
  getSiteComparison: (params) => api.get('/benchmarking/site-comparison', { params }),
  getShiftAnalysis: (params) => api.get('/benchmarking/shift-analysis', { params }),
  getPeriodComparison: (params) => api.get('/benchmarking/period-comparison', { params }),
  getZoneBenchmarking: (params) => api.get('/benchmarking/zone-benchmarking', { params }),
  getContractorComparison: (params) => api.get('/benchmarking/contractor-comparison', { params }),
};

export const workerAnalyticsAPI = {
  getWorkerPerformance: (params) => api.get('/analytics/worker-performance', { params }),
  getWorkerCheckIns: (params) => api.get('/analytics/worker-check-ins', { params }),
  getContractorPerformance: (params) => api.get('/analytics/contractor-performance', { params }),
  getContractorComparison: (params) => api.get('/analytics/contractor-comparison', { params }),
};

export const workersAPI = {
  getWorkers: (params) => api.get('/workers', { params }),
  getWorker: (id) => api.get(`/workers/${id}`),
  createWorker: (data) => api.post('/workers', data),
  updateWorker: (id, data) => api.put(`/workers/${id}`, data),
  deleteWorker: (id) => api.delete(`/workers/${id}`),
};

export const contractorsAPI = {
  getAll: (params) => api.get('/contractors', { params }),
  getOne: (id) => api.get(`/contractors/${id}`),
  create: (data) => api.post('/contractors', data),
  update: (id, data) => api.put(`/contractors/${id}`, data),
  delete: (id) => api.delete(`/contractors/${id}`),
};

export const usersAPI = {
  updateProfile: (id, data) => api.put(`/users/${id}`, data),
  updateNotificationPrefs: (id, prefs) => api.put(`/users/${id}/notification-prefs`, { notificationPrefs: prefs }),
};

export const authAPI = {
  changePassword: (data) => api.put('/auth/change-password', data),
};

export const detectProfilesAPI = {
  getProfiles: () => api.get('/settings/detection-profiles'),
  getProfile: (id) => api.get(`/settings/detection-profiles/${id}`),
};

export const devicesAPI = {
  getAIDevices: (params) => api.get('/cameras/ai-devices', { params }),
  getEngineSettings: (id) => api.get(`/cameras/ai-devices/${id}/engine-settings`),
  updateEngineSettings: (id, engineSettings) => api.patch(`/cameras/ai-devices/${id}/engine-settings`, { engineSettings }),
  restartAIDevice: (id, reason) => api.post(`/cameras/ai-devices/${id}/restart`, { reason }),
  getDevices: (params) => api.get('/devices', { params }),
  getDevice: (id) => api.get(`/devices/${id}`),
  createDevice: (data) => api.post('/devices', data),
  updateDevice: (id, data) => api.patch(`/devices/${id}`, data),
  updateAIDevice: (id, data) => api.patch(`/cameras/ai-device/${id}`, data),
  deleteDevice: (id) => api.delete(`/devices/${id}`),
  getDeviceConfig: (id, format = 'json') => api.get(`/devices/${id}/config`, { params: { format } }),
  regenerateCredentials: (id) => api.post(`/devices/${id}/regenerate-credentials`),
  getDeviceLogs: (id) => api.get(`/devices/${id}/logs`),
};
